import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { callBackend } from '../../../utils/api';
import {
    dispatchPortfolioImportApplied,
    portfolioImportPostApplyErrorMessage,
} from '../../../utils/portfolioImportEvents';
import { fetchLatestPortfolioImportStatus } from '../../../utils/importStatusClient';
import { shouldShowPerformancePendingBanner } from '../../../performance/portfolioSyncNotifications';
import {
    isPreviewStaleImportApplyError,
    isWatchlistStaleImportApplyError,
    parseCloudRunError,
    portfolioImportReviewVersionKey,
} from '../../msiDriftReviewHelpers';

export default function usePortfolioImports({
    userUid,
    activeView,
}) {
    const appliedImportRunIdsRef = useRef(new Set());
    const applyOwner = useMemo(() => ({}), [userUid]);
    const applyOwnerRef = useRef(applyOwner);
    applyOwnerRef.current = applyOwner;
    useEffect(() => {
        applyOwnerRef.current = applyOwner;
        setPerformancePendingReviewApplying(false);
        setPerformancePendingReviewError('');
        setPerformancePendingReviewOpen(false);
        setPerformancePendingReviewRun(null);
        return () => {
            if (applyOwnerRef.current === applyOwner) applyOwnerRef.current = null;
        };
    }, [applyOwner]);
    const [latestImportStatus, setLatestImportStatus] = useState(null);
    const [dismissedPerformancePendingRunId, setDismissedPerformancePendingRunId] = useState(null);
    const [performancePendingReviewOpen, setPerformancePendingReviewOpen] = useState(false);
    const [performancePendingReviewRun, setPerformancePendingReviewRun] = useState(null);
    const [performancePendingReviewApplying, setPerformancePendingReviewApplying] = useState(false);
    const [performancePendingReviewError, setPerformancePendingReviewError] = useState('');
    const [watchlistStaleReviewKeys, setWatchlistStaleReviewKeys] = useState(() => new Set());

    const fetchLatestImportStatus = useCallback(async (options = {}) => {
        if (!userUid) {
            setLatestImportStatus(null);
            return null;
        }

        try {
            const response = await fetchLatestPortfolioImportStatus({
                ...options,
                cacheKey: userUid,
            });
            setLatestImportStatus(response || null);
            return response || null;
        } catch (fetchError) {
            console.error('Failed to fetch latest portfolio import status:', fetchError);
            return null;
        }
    }, [userUid]);

    const markPerformanceRunWatchlistStale = useCallback((run) => {
        const runId = run?.id == null ? '' : String(run.id);
        const reviewKey = portfolioImportReviewVersionKey(run);
        if (!runId || !reviewKey) return;
        setWatchlistStaleReviewKeys((current) => {
            if (current.has(reviewKey)) return current;
            const next = new Set(current);
            next.add(reviewKey);
            return next;
        });
        setPerformancePendingReviewRun((current) => {
            if (current && portfolioImportReviewVersionKey(current) !== reviewKey) {
                return current;
            }
            return { ...(current || run), is_watchlist_stale: true };
        });
        setLatestImportStatus((current) => {
            const pending = current?.latest_pending_intraday;
            if (portfolioImportReviewVersionKey(pending) !== reviewKey) return current;
            return {
                ...current,
                latest_pending_intraday: {
                    ...pending,
                    is_watchlist_stale: true,
                },
            };
        });
    }, []);

    const pendingPerformanceImport = useMemo(() => {
        const run = latestImportStatus?.latest_pending_intraday;
        if (!run || run.status !== 'pending_apply') return null;
        if (!run.has_pending_state_change) return null;
        return watchlistStaleReviewKeys.has(portfolioImportReviewVersionKey(run))
            ? { ...run, is_watchlist_stale: true }
            : run;
    }, [latestImportStatus, watchlistStaleReviewKeys]);

    const showPerformancePendingBanner = useMemo(() => (
        shouldShowPerformancePendingBanner({
            activeView,
            pendingRun: pendingPerformanceImport,
            dismissedRunId: dismissedPerformancePendingRunId,
        })
    ), [activeView, dismissedPerformancePendingRunId, pendingPerformanceImport]);

    useEffect(() => {
        if (!userUid) return;
        if (!['overview', 'pos-sizing', 'performance'].includes(activeView)) return;
        void fetchLatestImportStatus();
    }, [activeView, fetchLatestImportStatus, userUid]);

    useEffect(() => {
        setWatchlistStaleReviewKeys(new Set());
    }, [userUid]);

    useEffect(() => {
        if (!userUid) return;
        const handler = () => {
            void fetchLatestImportStatus();
        };
        window.addEventListener('imports-updated', handler);
        window.addEventListener('portfolio-import-applied', handler);
        return () => {
            window.removeEventListener('imports-updated', handler);
            window.removeEventListener('portfolio-import-applied', handler);
        };
    }, [fetchLatestImportStatus, userUid]);

    useEffect(() => {
        if (pendingPerformanceImport?.id || performancePendingReviewApplying || performancePendingReviewError) return;
        setDismissedPerformancePendingRunId(null);
        setPerformancePendingReviewRun(null);
        setPerformancePendingReviewOpen(false);
        setPerformancePendingReviewError('');
    }, [pendingPerformanceImport?.id, performancePendingReviewApplying, performancePendingReviewError]);

    const handleReviewPerformancePendingImport = useCallback(async () => {
        if (!pendingPerformanceImport?.id) return;
        setPerformancePendingReviewError('');
        try {
            const run = await callBackend(`api/portfolio/imports/${pendingPerformanceImport.id}`, 'GET');
            setPerformancePendingReviewRun(run || pendingPerformanceImport);
        } catch (reviewError) {
            console.error('Failed to load pending Performance import:', reviewError);
            setPerformancePendingReviewRun(pendingPerformanceImport);
        } finally {
            setPerformancePendingReviewOpen(true);
        }
    }, [pendingPerformanceImport]);

    const handleApplyPerformancePendingImport = useCallback(async (run) => {
        const isCurrentOwner = () => applyOwnerRef.current === applyOwner;
        const appliedKey = `${userUid}:${run?.id}`;
        const runId = run?.id == null ? '' : String(run.id);
        const reviewKey = portfolioImportReviewVersionKey(run);
        if (
            !runId
            || !reviewKey
            || performancePendingReviewApplying
            || appliedImportRunIdsRef.current.has(appliedKey)
            || run?.is_watchlist_stale === true
            || watchlistStaleReviewKeys.has(reviewKey)
        ) return;
        setPerformancePendingReviewApplying(true);
        setPerformancePendingReviewError('');
        try {
            await callBackend(`api/portfolio/imports/${run.id}/apply`, 'POST', {
                expected_staged_state_hash: run?.source_metadata_json?.staged_state_hash ?? null,
            });
            if (!isCurrentOwner()) return;
            appliedImportRunIdsRef.current.add(appliedKey);
            setPerformancePendingReviewRun({ ...run, status: 'applied' });
            await dispatchPortfolioImportApplied({ userUid });
            if (!isCurrentOwner()) return;
            setPerformancePendingReviewOpen(false);
            setPerformancePendingReviewRun(null);
            setDismissedPerformancePendingRunId(null);
            window.dispatchEvent(new CustomEvent('imports-updated'));
            await fetchLatestImportStatus();
        } catch (applyError) {
            if (!isCurrentOwner()) return;
            console.error('Failed to apply pending Performance import:', applyError);
            if (appliedImportRunIdsRef.current.has(appliedKey)) {
                setPerformancePendingReviewError(portfolioImportPostApplyErrorMessage(applyError));
                window.dispatchEvent(new CustomEvent('imports-updated'));
                return;
            }
            const parsedError = parseCloudRunError(applyError);
            setPerformancePendingReviewError(
                parsedError.message
                || 'Failed to apply import. Please try again.',
            );
            if (isWatchlistStaleImportApplyError(parsedError)) {
                markPerformanceRunWatchlistStale(run);
                try {
                    const refreshedRun = await callBackend(
                        `api/portfolio/imports/${run.id}`,
                        'GET',
                    );
                    if (!isCurrentOwner()) return;
                    setPerformancePendingReviewRun(
                        portfolioImportReviewVersionKey(refreshedRun) === reviewKey
                            ? { ...(refreshedRun || run), is_watchlist_stale: true }
                            : (refreshedRun || run),
                    );
                } catch (_refreshError) {
                    // The reviewed version remains blocked even if refresh fails.
                }
                await fetchLatestImportStatus({ force: true });
            } else if (isPreviewStaleImportApplyError(parsedError)) {
                try {
                    const refreshedRun = await callBackend(
                        `api/portfolio/imports/${run.id}`,
                        'GET',
                    );
                    if (!isCurrentOwner()) return;
                    setPerformancePendingReviewRun(refreshedRun || run);
                    await fetchLatestImportStatus({ force: true });
                } catch (_refreshError) {
                    // Keep the stale token so another Apply remains fail-closed.
                }
            }
        } finally {
            if (isCurrentOwner()) setPerformancePendingReviewApplying(false);
        }
    }, [
        applyOwner,
        fetchLatestImportStatus,
        markPerformanceRunWatchlistStale,
        performancePendingReviewApplying,
        userUid,
        watchlistStaleReviewKeys,
    ]);

    const handleIgnorePerformancePendingImport = useCallback(async (run) => {
        if (!run?.id) return;
        setPerformancePendingReviewError('');
        try {
            await callBackend(`api/portfolio/imports/${run.id}/ignore`, 'POST');
            setPerformancePendingReviewOpen(false);
            setPerformancePendingReviewRun(null);
            setDismissedPerformancePendingRunId(String(run.id));
            window.dispatchEvent(new CustomEvent('imports-updated'));
            await fetchLatestImportStatus();
        } catch (ignoreError) {
            console.error('Failed to ignore pending Performance import:', ignoreError);
        }
    }, [fetchLatestImportStatus]);

    return {
        latestImportStatus,
        pendingPerformanceImport,
        showPerformancePendingBanner,
        dismissedPerformancePendingRunId,
        setDismissedPerformancePendingRunId,
        performancePendingReviewOpen,
        setPerformancePendingReviewOpen,
        performancePendingReviewRun,
        performancePendingReviewApplying,
        performancePendingReviewError,
        handleReviewPerformancePendingImport,
        handleApplyPerformancePendingImport,
        handleIgnorePerformancePendingImport,
        fetchLatestImportStatus,
    };
}
